import { css } from '@emotion/react';
import cn from 'classnames';
import { noop } from 'lodash';
import RcTable, { type ColumnType, type TableProps as RcTableProps } from 'rc-table';
import { memo, type ReactNode, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TYPOGRAPHY_VARIANTS } from '../../../config/typography/constants';
import { useToggleSort } from '../../../utils/hooks/useToggleSort';
import { Checkbox } from '../../forms/checkbox/Checkbox';
import { ImpulseSpinner } from '../../general/loading/impulse-spinner/ImpulseSpinner';
import { Tooltip } from '../../general/tooltip/Tooltip';
import { TABLE_VARIANTS } from './constants';
import { HeaderCell } from './header-cell/HeaderCell';
import messages from './Table.messages';
import styles from './Table.styles';

const SELECTION_COLUMN_KEY = '__selection__';
const SELECTION_COLUMN_WIDTH = '48px';

const DEFAULT_SELECTED_ROW_KEYS: string[] = [];

/**
 * Resolves the empty-state node of the table.
 * @param loading Whether the table is loading.
 * @param error Optional error node.
 * @param emptyText Fallback empty label.
 * @returns Node displayed when there is no row.
 */
const getEmptyText = (
  loading: boolean,
  error?: ReactNode,
  emptyText?: ReactNode | (() => ReactNode),
): ReactNode | (() => ReactNode) => {
  if (error) {
    return <span css={styles.error}>{error}</span>;
  }

  if (loading) {
    return (
      <div
        css={(theme) => css`
          min-height: ${theme.typography[TYPOGRAPHY_VARIANTS.BODY_MEDIUM_REGULAR].lineHeight};
          display: flex;
          align-items: center;
        `}
      >
        <ImpulseSpinner size={ImpulseSpinner.SIZES.SMALL} />
      </div>
    );
  }

  return emptyText;
};

/**
 * Returns whether a table record can be keyed for selection.
 * Placeholder rows (for example the class-form "add exam" `null` row) are skipped.
 * @param record Table row candidate.
 * @returns True when the record is a non-null object.
 */
function isSelectableRecord<RecordType>(record: RecordType): record is NonNullable<RecordType> {
  return record != null;
}

/**
 * Resolves the row key of a record from the Table `rowKey` prop.
 * @param record Table row.
 * @param rowKey Column name or getter used as unique row identifier.
 * @returns Stable key for the row, or an empty string when the record is a placeholder.
 */
function getRecordKey<RecordType>(
  record: RecordType,
  rowKey?: RcTableProps<RecordType>['rowKey'],
): string {
  if (!isSelectableRecord(record)) {
    return '';
  }

  if (typeof rowKey === 'function') {
    return String(rowKey(record));
  }

  const keyName = rowKey == null ? 'key' : String(rowKey);

  return String((record as Record<string, unknown>)[keyName]);
}

// Add custom props to our own ColumnType.
type RcColumnType<RecordType> = ColumnType<RecordType> & {
  sortable?: boolean;
};

export type TableRowSelection<RecordType> = {
  /** Keys of currently selected rows, including rows from other pages. */
  selectedRowKeys?: string[];
  /**
   * Called after a row or header checkbox changes.
   * @param selectedRowKeys Next selected keys.
   * @param selectedRows Selected records present on the current page.
   */
  onChange?: (selectedRowKeys: string[], selectedRows: RecordType[]) => void;
  /** Disables every selection checkbox. Used while the table data is being refreshed. */
  disabled?: boolean;
};

export type TableProps<RecordType> = Omit<RcTableProps<RecordType>, 'columns'> & {
  /** Definition of columns. */
  columns?: RcColumnType<RecordType>[];
  /** Display a loader. */
  isLoading?: boolean;
  /** Error message. */
  error?: ReactNode;
  /** Current information regarding the sorting of the table. */
  currentSort?: string;
  /** Function for mutating the sorting of the table. */
  setCurrentSort?: (sort: string) => void;
  /** Theme variant. */
  variant?: TABLE_VARIANTS;
  /** When set, prepends a checkbox column so rows can be selected. */
  rowSelection?: TableRowSelection<RecordType>;
};

const DEFAULT_COLUMNS = [];
const DEFAULT_DATA = [];

// biome-ignore lint/complexity/noUselessTypeConstraint: this is need to avoid syntax error during testing
export const TableBase = <RecordType extends unknown = unknown>({
  className = null,
  columns = DEFAULT_COLUMNS,
  data = DEFAULT_DATA,
  isLoading = false,
  error = null,
  emptyText = <FormattedMessage {...messages.NO_DATA} />,
  currentSort = null,
  setCurrentSort = noop,
  variant = TABLE_VARIANTS.DARK,
  rowSelection = undefined,
  rowKey,
  ...props
}: TableProps<RecordType>) => {
  const intl = useIntl();
  const toggleSort = useToggleSort(currentSort, setCurrentSort);

  const selectedRowKeys = rowSelection?.selectedRowKeys ?? DEFAULT_SELECTED_ROW_KEYS;
  const onSelectionChange = rowSelection?.onChange ?? noop;
  const isSelectionDisabled = Boolean(rowSelection?.disabled);

  const pageRowKeys = useMemo((): string[] => {
    if (!rowSelection) {
      return [];
    }

    return (data ?? [])
      .filter((record) => isSelectableRecord(record))
      .map((record) => getRecordKey(record, rowKey));
  }, [data, rowKey, rowSelection]);

  const selectedRowKeysSet = useMemo(() => new Set(selectedRowKeys), [selectedRowKeys]);

  const pageAllChecked =
    pageRowKeys.length > 0 && pageRowKeys.every((key) => selectedRowKeysSet.has(key));
  const pageIndeterminate =
    pageRowKeys.some((key) => selectedRowKeysSet.has(key)) && !pageAllChecked;

  const emitSelectionChange = useCallback(
    (nextSelectedRowKeys: string[]): void => {
      const nextSelectedKeysSet = new Set(nextSelectedRowKeys);
      const selectedRows = (data ?? []).filter(
        (record) =>
          isSelectableRecord(record) && nextSelectedKeysSet.has(getRecordKey(record, rowKey)),
      );

      onSelectionChange(nextSelectedRowKeys, selectedRows);
    },
    [data, onSelectionChange, rowKey],
  );

  const onToggleRow = useCallback(
    (key: string, checked: boolean): void => {
      if (isSelectionDisabled) {
        return;
      }

      if (checked) {
        emitSelectionChange(
          selectedRowKeys.includes(key) ? selectedRowKeys : [...selectedRowKeys, key],
        );
        return;
      }

      emitSelectionChange(selectedRowKeys.filter((selectedKey) => selectedKey !== key));
    },
    [emitSelectionChange, isSelectionDisabled, selectedRowKeys],
  );

  const onTogglePage = useCallback(
    (checked: boolean): void => {
      if (isSelectionDisabled) {
        return;
      }

      if (checked) {
        const nextSelectedRowKeys = [...selectedRowKeys];

        for (const key of pageRowKeys) {
          if (!selectedRowKeysSet.has(key)) {
            nextSelectedRowKeys.push(key);
          }
        }

        emitSelectionChange(nextSelectedRowKeys);
        return;
      }

      emitSelectionChange(selectedRowKeys.filter((key) => !pageRowKeys.includes(key)));
    },
    [emitSelectionChange, isSelectionDisabled, pageRowKeys, selectedRowKeys, selectedRowKeysSet],
  );

  const selectionColumn = useMemo<RcColumnType<RecordType> | null>(() => {
    if (!rowSelection) {
      return null;
    }

    return {
      key: SELECTION_COLUMN_KEY,
      width: SELECTION_COLUMN_WIDTH,
      title: (
        <Tooltip content={<FormattedMessage {...messages.SELECT_ALL} />}>
          <div>
            <Checkbox
              checked={pageAllChecked}
              indeterminate={pageIndeterminate}
              onChange={onTogglePage}
              disabled={isSelectionDisabled}
              aria-label={intl.formatMessage(messages.SELECT_ALL)}
            />
          </div>
        </Tooltip>
      ),
      render: (_value: unknown, record: RecordType) => {
        if (!isSelectableRecord(record)) {
          return null;
        }

        const key = getRecordKey(record, rowKey);

        return (
          <Checkbox
            checked={selectedRowKeysSet.has(key)}
            onChange={(checked) => onToggleRow(key, checked)}
            disabled={isSelectionDisabled}
            aria-label={intl.formatMessage(messages.SELECT_ROW)}
          />
        );
      },
    };
  }, [
    intl,
    isSelectionDisabled,
    onTogglePage,
    onToggleRow,
    pageAllChecked,
    pageIndeterminate,
    rowKey,
    rowSelection,
    selectedRowKeysSet,
  ]);

  const enhancedColumns = useMemo(
    () =>
      (selectionColumn ? [selectionColumn, ...columns] : columns).map((col) => {
        if (col.key === SELECTION_COLUMN_KEY) {
          return col;
        }

        return {
          ...col,
          title: (
            <HeaderCell
              name={`${col.key}`}
              title={col.title}
              toggleSort={toggleSort}
              sortable={col.sortable}
              sort={currentSort}
              align={col.align}
            />
          ),
        };
      }),
    [columns, currentSort, selectionColumn, toggleSort],
  );

  return (
    <RcTable
      {...props}
      rowKey={rowKey}
      columns={enhancedColumns}
      data={isLoading || error ? null : data}
      emptyText={getEmptyText(isLoading, error, emptyText)}
      className={cn(className, variant)}
      css={styles.table}
    />
  );
};

TableBase.displayName = 'Table';

// Cast as typeof TableBase to keep the generic type on Table.
export const Table = memo(TableBase) as unknown as typeof TableBase & {
  VARIANTS?: typeof TABLE_VARIANTS;
};

Table.VARIANTS = TABLE_VARIANTS;
